import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  Layers,
  Newspaper,
  Save,
  Star,
  Trash2,
  X,
} from "lucide-react";
import {
  factorsApi,
  strategiesApi,
  tradesApi,
  type FactorRecord,
  type Strategy,
  type TradeDetail,
} from "../lib/api";
import { formatCurrency, formatDate, formatSignedCurrency } from "../lib/format";
import { useI18n } from "../lib/i18n";

export default function TradeDetailPage() {
  const { t: tr } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tradeId = Number(id);

  const tradeQ = useQuery({
    queryKey: ["trade", tradeId],
    queryFn: () => tradesApi.get(tradeId),
    enabled: Number.isFinite(tradeId),
  });
  const stratsQ = useQuery({ queryKey: ["strategies"], queryFn: () => strategiesApi.list() });
  const factorsQ = useQuery({ queryKey: ["factors"], queryFn: () => factorsApi.list() });

  const destroyM = useMutation({
    mutationFn: () => tradesApi.destroy(tradeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["wallet"] });
      navigate("/trades");
    },
  });

  const t = tradeQ.data?.trade;

  if (tradeQ.isLoading) return <p className="text-parchment-muted">{tr("common.loading")}</p>;
  if (tradeQ.isError || !t) {
    return (
      <p className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-pnl-neg">
        {tr("trade.notFound")}
      </p>
    );
  }

  const isOpen = t.status === "open";
  const cls = isOpen
    ? "text-amber-300"
    : t.net_pl > 0
      ? "text-pnl-pos"
      : t.net_pl < 0
        ? "text-pnl-neg"
        : "text-parchment";

  return (
    <>
      <header className="flex flex-wrap items-center gap-3">
        <Link to="/trades" className="btn-ghost">
          <ArrowLeft className="w-4 h-4" />
          {tr("common.back")}
        </Link>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => {
            if (confirm(tr("trades.confirmDelete"))) destroyM.mutate();
          }}
          disabled={destroyM.isPending}
          className="btn-ghost text-pnl-neg hover:!text-rose-100"
        >
          <Trash2 className="w-4 h-4" />
          {tr("common.delete")}
        </button>
      </header>

      {/* Hero */}
      <section className="glass-gold p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span
                className={
                  "inline-flex items-center rounded-md px-2 py-1 text-[11px] font-bold uppercase tracking-widest " +
                  (t.side === "Buy"
                    ? "bg-emerald-500/15 text-pnl-pos"
                    : "bg-rose-500/15 text-pnl-neg")
                }
              >
                {t.side}
              </span>
              <span className="font-mono text-2xl font-bold text-parchment">
                {t.symbol || "—"}
              </span>
              {isOpen && <span className="chip-gold text-[10px] px-2 py-0.5">Open</span>}
            </div>
            <p className={`text-4xl font-black tabular ${cls}`}>{formatSignedCurrency(t.net_pl)}</p>
            <p className="text-xs text-parchment-muted mt-1">
              {tr("trade.grossLine", {
                gross: formatSignedCurrency(t.profit),
                commission: formatCurrency(t.commission),
                swap: formatSignedCurrency(-t.swap),
              })}
            </p>
          </div>
          <StarRow value={t.stars ?? 0} />
        </div>
      </section>

      {/* Timing + prices */}
      <section className="grid gap-4 lg:grid-cols-2">
        <div className="glass p-5">
          <h3 className="label mb-3">{tr("trade.timing")}</h3>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <F label={tr("trade.open")} value={formatDate(t.open_time)} />
            <F label={tr("trade.close")} value={isOpen ? "—" : formatDate(t.close_time)} />
            <F label={tr("trade.duration")} value={humanDuration(t.open_time, t.close_time)} />
            <F label={tr("trade.volumeLots")} value={t.volume != null ? t.volume.toString() : "—"} />
          </dl>
        </div>
        <div className="glass p-5">
          <h3 className="label mb-3">{tr("trade.prices")}</h3>
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <F label={tr("trade.open")} value={t.open_price != null ? t.open_price.toFixed(5) : "—"} />
            <F label={tr("trade.close")} value={t.close_price != null ? t.close_price.toFixed(5) : "—"} />
            <F
              label={tr("trade.stopLoss")}
              value={t.sl != null ? t.sl.toFixed(5) : "—"}
              valueCls={t.sl != null ? "text-pnl-neg" : "text-parchment-muted"}
            />
            <F
              label={tr("trade.takeProfit")}
              value={t.tp != null ? t.tp.toFixed(5) : "—"}
              valueCls={t.tp != null ? "text-pnl-pos" : "text-parchment-muted"}
            />
            <F label={tr("trade.balanceAfter")} value={t.balance_after != null ? formatCurrency(t.balance_after) : "—"} />
            <F
              label={tr("trade.sizeClass")}
              value={t.size_class}
              valueCls={
                t.size_class === "over"
                  ? "text-pnl-neg"
                  : t.size_class === "normal"
                    ? "text-pnl-pos"
                    : "text-parchment-muted"
              }
            />
          </dl>
        </div>
      </section>

      {/* Strategies + Factors */}
      <StrategyBoard trade={t} strategies={stratsQ.data?.strategies ?? []} />
      <FactorBoard trade={t} factors={factorsQ.data?.factors ?? []} />

      <NewsTradeCard trade={t} />
      <NotesEditor trade={t} />
    </>
  );
}

function humanDuration(openIso: string | null, closeIso: string | null): string {
  if (!openIso || !closeIso) return "—";
  const a = new Date(openIso).getTime();
  const b = new Date(closeIso).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return "—";
  const mins = Math.round((b - a) / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMin = mins % 60;
  if (hrs < 24) return `${hrs}h ${remMin}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

function F({ label, value, valueCls }: { label: string; value: string; valueCls?: string }) {
  return (
    <div>
      <dt className="label">{label}</dt>
      <dd className={`mt-0.5 tabular ${valueCls ?? "text-parchment"}`}>{value}</dd>
    </div>
  );
}

function StarRow({ value }: { value: number }) {
  const { t } = useI18n();
  const stars = Array.from({ length: 5 }).map((_, i) => i < value);
  return (
    <div className="flex flex-col items-end">
      <p className="label">{t("trade.quality")}</p>
      <div className="flex gap-0.5 mt-1">
        {stars.map((filled, i) => (
          <Star
            key={i}
            className={
              "w-5 h-5 " + (filled ? "fill-gold-300 text-accent" : "text-parchment-muted/30")
            }
          />
        ))}
      </div>
      <p className="text-xs text-parchment-muted mt-1">{t("trade.starsAuto", { value })}</p>
    </div>
  );
}

function StrategyBoard({
  trade,
  strategies,
}: {
  trade: TradeDetail;
  strategies: Strategy[];
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const setM = useMutation({
    mutationFn: (ids: number[]) => tradesApi.setStrategies(trade.id, ids),
    onSuccess: (d) => {
      qc.setQueryData(["trade", trade.id], d);
      qc.invalidateQueries({ queryKey: ["trades"] });
    },
  });

  const selected = new Set(trade.strategy_ids);
  const active = strategies.filter((s) => s.status === "active");

  return (
    <section className="glass p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-parchment flex items-center gap-2">
          <Layers className="w-5 h-5 text-accent" />
          {t("trade.strategies")}
        </h3>
        <Link to="/settings" className="text-xs text-accent hover:text-accent">
          {t("trade.manage")}
        </Link>
      </div>
      {active.length === 0 ? (
        <p className="text-parchment-muted text-sm">
          {t("trade.emptyStrategies")}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {active.map((s) => {
            const on = selected.has(s.id);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  const next = new Set(selected);
                  if (on) next.delete(s.id);
                  else next.add(s.id);
                  setM.mutate(Array.from(next));
                }}
                disabled={setM.isPending}
                className={
                  "rounded-full px-3 py-1.5 text-xs font-semibold border transition " +
                  (on
                    ? "bg-gold-500/15 border-gold-500/40 text-accent"
                    : "bg-surface-1 border-glass-mid text-parchment-muted hover:bg-surface-2")
                }
              >
                {on && <Check className="inline w-3 h-3 me-1" />}
                {s.name}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

function FactorBoard({
  trade,
  factors,
}: {
  trade: TradeDetail;
  factors: FactorRecord[];
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const setM = useMutation({
    mutationFn: ({ id, value }: { id: number; value: "yes" | "no" | null }) =>
      tradesApi.setFactor(trade.id, id, value),
    onSuccess: (d) => {
      qc.setQueryData(["trade", trade.id], d);
      qc.invalidateQueries({ queryKey: ["trades"] });
    },
  });

  const map = new Map<number, "yes" | "no">();
  for (const f of trade.factors) map.set(f.id, f.value);

  return (
    <section className="glass p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-parchment">{t("trade.factors")}</h3>
        <Link to="/settings" className="text-xs text-accent hover:text-accent">
          {t("trade.manage")}
        </Link>
      </div>
      {factors.length === 0 ? (
        <p className="text-parchment-muted text-sm">
          {t("trade.emptyFactors")}
        </p>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {factors.map((f) => {
            const cur = map.get(f.id) ?? null;
            const isPos = f.kind === "positive";
            return (
              <li
                key={f.id}
                className="flex items-center gap-3 rounded-lg border border-glass bg-surface-1 p-3"
              >
                <span className="flex-1 min-w-0">
                  <p className="text-parchment font-medium truncate">{f.label}</p>
                  <p className="text-[10px] text-parchment-muted uppercase tracking-wider">
                    {isPos ? t("trade.positive") : t("trade.negative")}
                  </p>
                </span>
                <div className="flex gap-1">
                  <TriBtn
                    active={cur === "yes"}
                    good={isPos}
                    onClick={() => setM.mutate({ id: f.id, value: cur === "yes" ? null : "yes" })}
                    disabled={setM.isPending}
                    label={t("trade.yes")}
                    Icon={Check}
                  />
                  <TriBtn
                    active={cur === "no"}
                    good={!isPos}
                    onClick={() => setM.mutate({ id: f.id, value: cur === "no" ? null : "no" })}
                    disabled={setM.isPending}
                    label={t("trade.no")}
                    Icon={X}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function TriBtn({
  active,
  good,
  onClick,
  disabled,
  label,
  Icon,
}: {
  active: boolean;
  good: boolean;
  onClick: () => void;
  disabled?: boolean;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
}) {
  const activeCls = good
    ? "bg-emerald-500/15 border-emerald-500/40 text-pnl-pos"
    : "bg-rose-500/15 border-rose-500/40 text-pnl-neg";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "rounded-md px-2.5 py-1 text-xs font-semibold border transition inline-flex items-center gap-1 " +
        (active
          ? activeCls
          : "bg-surface-1 border-glass-mid text-parchment-muted hover:bg-surface-2")
      }
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}

/**
 * News-trading flag: was this taken around a news release?
 *
 * Saves on toggle rather than behind a Save button -- it is a single boolean
 * and an unsaved checkbox reads as already applied. Kept out of the factor
 * board on purpose: factors are typed positive/negative and drive 60% of the
 * quality score, while trading the news is neutral context.
 */
function NewsTradeCard({ trade }: { trade: TradeDetail }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: (next: boolean) => tradesApi.update(trade.id, { news_trade: next }),
    onSuccess: () => {
      setMsg(t("trade.saved"));
      qc.invalidateQueries({ queryKey: ["trade", trade.id] });
      qc.invalidateQueries({ queryKey: ["trades"] });
      setTimeout(() => setMsg(null), 2000);
    },
  });

  return (
    <section className="glass p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-parchment flex items-center gap-2">
          <Newspaper className="w-5 h-5 text-accent" />
          {t("trade.newsTrading")}
        </h3>
        {msg && <span className="text-xs text-pnl-pos">{msg}</span>}
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={trade.news_trade}
          disabled={m.isPending}
          onChange={(e) => m.mutate(e.target.checked)}
          className="accent-gold-500 w-4 h-4 mt-0.5 cursor-pointer disabled:opacity-40"
        />
        <span>
          <span className="text-sm font-semibold text-parchment block">
            {t("trade.newsTradingLabel")}
          </span>
          <span className="text-xs text-parchment-muted">{t("trade.newsTradingHelp")}</span>
        </span>
      </label>
    </section>
  );
}

function NotesEditor({ trade }: { trade: TradeDetail }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [notes, setNotes] = useState(trade.notes ?? "");
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => setNotes(trade.notes ?? ""), [trade.notes]);

  const m = useMutation({
    mutationFn: () => tradesApi.update(trade.id, { notes }),
    onSuccess: () => {
      setMsg(t("trade.saved"));
      qc.invalidateQueries({ queryKey: ["trade", trade.id] });
      qc.invalidateQueries({ queryKey: ["trades"] });
      setTimeout(() => setMsg(null), 2000);
    },
  });

  return (
    <section className="glass p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-parchment">{t("trade.notes")}</h3>
        {msg && <span className="text-xs text-pnl-pos">{msg}</span>}
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={4}
        className="input resize-y"
        placeholder={t("trade.notesPlaceholder")}
      />
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={() => m.mutate()}
          disabled={m.isPending || notes === (trade.notes ?? "")}
          className="btn-gold"
        >
          <Save className="w-4 h-4" />
          {t("trade.saveNotes")}
        </button>
      </div>
    </section>
  );
}
